// Time-based animation: a shared requestAnimationFrame ticker and the trace
// player that draws the curve progressively (the bottom dock's Play button).
// The player's stepping is pure so it can be tested without a browser.
import type { DrawRange } from './render/range.ts';

export type Speed = 0.25 | 0.5 | 1 | 2 | 4;
export const SPEEDS: Speed[] = [0.25, 0.5, 1, 2, 4];

/** Seconds a full drawing takes at speed 1 (matches the Curve Atlas page). */
export const DRAW_SECONDS = 14;
/** Pause at the end of an iteration before Auto next advances (ms). */
export const AUTO_NEXT_DELAY_MS = 900;
/** Where a replay restarts: just past the start so the tip is visible. */
export const REPLAY_EPS = 0.001;

export interface PlayerState {
  range: DrawRange;
  playing: boolean;
  autoNext: boolean;
  speed: Speed;
  /** Timestamp (ms) at which Auto next fires, 0 when not armed. */
  advanceAt: number;
}

export type PlayerEvent = 'draw' | 'finished' | 'advance';

/**
 * Advance the player by `dt` seconds at time `now` (ms). Mutates `s.range.end`
 * and `s.advanceAt`; returns what happened so the caller can redraw, stop or
 * step to the next iteration. `hasNext` says whether Auto next can advance.
 */
export function stepPlayer(s: PlayerState, dt: number, now: number, hasNext = false): PlayerEvent | null {
  if (!s.playing) return null;
  if (s.range.end < 1) {
    s.range.end = Math.min(1, s.range.end + dt * s.speed / DRAW_SECONDS);
    if (s.range.end < 1) return 'draw';
  }
  // reached the end
  if (s.autoNext && hasNext) {
    if (!s.advanceAt) { s.advanceAt = now + AUTO_NEXT_DELAY_MS; return 'draw'; }
    if (now < s.advanceAt) return null;
    s.advanceAt = 0;
    s.range.end = Math.min(1, s.range.start + REPLAY_EPS);
    return 'advance';
  }
  s.playing = false;
  s.advanceAt = 0;
  return 'finished';
}

/** Rewind for a replay: the drawing restarts from the range start. */
export function rewind(s: PlayerState): void {
  s.advanceAt = 0;
  s.range.end = Math.min(1, s.range.start + REPLAY_EPS);
}

/** One rAF loop shared by everything that animates; runs only while a source is active. */
export class Ticker {
  private sources = new Map<string, (dt: number, now: number) => void>();
  private raf = 0;
  private last = 0;

  set(id: string, fn: ((dt: number, now: number) => void) | null): void {
    if (fn) this.sources.set(id, fn); else this.sources.delete(id);
    if (this.sources.size && !this.raf) { this.last = 0; this.raf = requestAnimationFrame(this.tick); }
    if (!this.sources.size && this.raf) { cancelAnimationFrame(this.raf); this.raf = 0; }
  }

  has(id: string): boolean { return this.sources.has(id); }

  private tick = (now: number) => {
    this.raf = 0;
    const dt = this.last ? Math.min(0.06, Math.max(0, (now - this.last) / 1000)) : 0;
    this.last = now;
    for (const fn of [...this.sources.values()]) fn(dt, now);
    if (this.sources.size) this.raf = requestAnimationFrame(this.tick);
  };
}
